/**
 * @file menu-link-types.ts
 * @description Виды пунктов меню для кабинета (phase19.16 Р1; SB15, SB75;
 * uiux-design-r1 §3 «Окно пункта меню»): подпись вида в строке и в окне,
 * какой список целей нужен, только ли наверху.
 *
 * Реестр (OCP): новый вид пункта — запись здесь и в `SITE_MENU_LINK_TYPES`
 * пакета shared; окно пункта и строка списка читают реестр, веток по видам
 * в разметке нет. Все виды shared обязаны быть в реестре (проверяет тест).
 */

import { SITE_MENU_TARGET_FIELD, type LegalDocumentKind, type SiteMenuLinkType, type SiteSystemRoute } from "@chaiberi/shared";

/** Список целей для выбора в окне пункта. */
export type MenuTargetList = "pages" | "categories" | "contentCategories" | "contentTags" | "legalDocuments";

export interface MenuLinkTypeMeta {
  /** Подпись в строке списка: «Страница», «Категория». */
  short: string;
  /** Подпись варианта в окне: «Страница сайта». */
  label: string;
  /** Список целей; нет — пункт без выбора цели (группа, адрес, раздел). */
  list?: MenuTargetList;
  /** Только на верхнем уровне (колонка подвала, автосписок категорий). */
  topOnly?: boolean;
  /** Показывать вариант в окне шапки, подвала или обоих. */
  locations: ReadonlyArray<"HEADER" | "FOOTER">;
}

const BOTH = ["HEADER", "FOOTER"] as const;

export const MENU_LINK_TYPE_META: Readonly<Record<SiteMenuLinkType, MenuLinkTypeMeta>> = {
  SITE_PAGE: { short: "Страница", label: "Страница сайта", list: "pages", locations: BOTH },
  CATEGORY: { short: "Категория", label: "Категория каталога", list: "categories", locations: BOTH },
  SYSTEM: { short: "Раздел", label: "Раздел: Главная, Каталог, Блог", locations: BOTH },
  CONTENT_CATEGORY: { short: "Рубрика блога", label: "Рубрика блога", list: "contentCategories", locations: BOTH },
  CONTENT_TAG: { short: "Тег блога", label: "Тег блога", list: "contentTags", locations: BOTH },
  // Партия «Юридические документы» (uiux-legal-steps-b-c §8.1): цель — документ или группа.
  LEGAL_DOCUMENT: { short: "Документ", label: "Документ или группа документов", list: "legalDocuments", locations: BOTH },
  URL: { short: "Адрес", label: "Другой сайт или адрес", locations: BOTH },
  GROUP: { short: "Группа", label: "Группа (колонка) без ссылки", topOnly: true, locations: BOTH },
  CATALOG_CATEGORIES: {
    short: "Категории каталога",
    label: "Категории каталога автоматически",
    topOnly: true,
    locations: BOTH,
  },
};

/** Разделы платформы для пункта «Раздел» (SB75: блог — «Лента блога»). */
export const SYSTEM_ROUTE_LABELS: Readonly<Record<SiteSystemRoute, string>> = {
  HOME: "Главная",
  CATALOG: "Каталог",
  BLOG: "Лента блога",
};

/**
 * Подпись вида в строке списка меню. Цель «документ» — группа: «Группа
 * документов» (проект данных §6.2); вид цели ещё не известен (список
 * документов не загружен) — «Документ».
 */
export function menuTypeShort(linkType: SiteMenuLinkType, legalKind: LegalDocumentKind | null = null): string {
  if (linkType === "LEGAL_DOCUMENT" && legalKind === "GROUP") return "Группа документов";
  return MENU_LINK_TYPE_META[linkType].short;
}

/** Поле цели для вида (или `null` — у вида нет цели). */
export function targetField(type: SiteMenuLinkType): string | null {
  return type in SITE_MENU_TARGET_FIELD ? SITE_MENU_TARGET_FIELD[type as keyof typeof SITE_MENU_TARGET_FIELD] : null;
}

/** Длинная подпись займёт много места в шапке (uiux §3). */
export const MENU_LABEL_SOFT_MAX = 20;
/** На ПК в строку помещается 6–7 пунктов (uiux §3). */
export const MENU_TOP_SOFT_MAX = 7;
